import React, { Fragment } from 'react';
import { Location } from 'history';
import { t } from 'i18next';
import { get } from 'lodash';
import { useQueryClient } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAccessControl } from 'utils/access-control';
import { AccountUserType, BreadcrumpDataType } from '../../types';
import { getText } from '../../text';
import Icon from '../Icon';

const { settings } = getText('EN');

type Path = {
  path: string;
  label: string;
};

type LocationPath = {
  children?: Array<LocationPath>;
  path: string;
  label: string;
};

const BreadcrumpsConfig: Array<LocationPath> = [
  {
    path: '/dashboard',
    label: 'sidebar.dashboard',
  },
  {
    path: '/analytics',
    label: 'sidebar.analytics',
  },
  {
    path: '/workflow',
    label: 'sidebar.workflows',
    children: [
      {
        path: '/edit',
        label: 'edit',
      },
      {
        path: '/create',
        label: 'new',
      },
    ],
  },
  {
    path: '/settings',
    label: settings.heading,
    children: [
      {
        path: '/limit',
        label: settings.surveylimit.layout.heading,
      },
      {
        path: '/templates',
        label: settings.savedtemplates.layout.heading,
        children: [
          {
            path: '/create',
            label: 'settings.breadcrumps.new_template',
          },
          {
            path: '/*',
            label: 'settings.breadcrumps.edit_template',
          },
        ],
      },
      {
        path: '/user',
        label: settings.user.layout.heading,
      },
      // {
      //   path: '/blocklist',
      //   label: settings.blocklist.layout.heading
      // },
      {
        path: '/customfields',
        label: settings.customfields.layout.heading,
      },
      // {
      //   path: '/tags',
      //   label: settings.tags.layout.heading
      // },
      {
        path: '/design-themes',
        label: settings.designThemes.layout.heading,
      },
      {
        path: '/dkim',
        label: settings.dkimsettings.layout.heading,
      },
      {
        path: '/custom-domain',
        label: settings.customdomainmapping.layout.heading,
      },
      {
        path: '/account',
        label: settings.accountinfo.layout.heading,
      },
      // {
      //   path: '/integrations',
      //   label: settings.integrations.layout.heading,
      //   children: listOfIntegrations.map(
      //     (integrationItem: IntegrationItemType) => ({
      //       path: `/${integrationItem.href}`,
      //       label: integrationItem.name
      //     })
      //   )
      // }
      // {
      //   path: '/accountinfo',
      //   label: settings.accountinfo.layout.heading
      // }
    ],
  },
  {
    path: '/survey',
    label: 'sidebar.surveys',
    children: [
      {
        path: '/create',
        label: 'new',
      },
    ],
  },
  {
    path: '/response',
    label: 'sidebar.responses',
  },
  {
    path: '/queue',
    label: 'sidebar.queue',
  },
  {
    path: '/contacts',
    label: 'sidebar.contacts.title',
    children: [
      {
        path: '/edit',
        label: 'edit',
      },
      {
        path: '/create',
        label: 'new_contact',
      },
      {
        path: '/*',
        label: 'detailed_view',
      },
    ],
  },
  {
    path: '/lists',
    label: 'sidebar.contacts.lists',
    children: [
      {
        path: '/edit',
        label: 'edit',
      },
      {
        path: '/create',
        label: 'new',
      },
    ],
  },
  {
    path: '/segments',
    label: 'sidebar.contacts.segments',
    children: [
      {
        path: '/create',
        label: 'segments.add_new_segments',
      },
    ],
  },
  {
    path: '/profile',
    label: settings.profile.layout.heading,
  },
];

type LocationBreadcrumpProps = {
  setBreadcrumpData?: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  breadcrumpData?: BreadcrumpDataType;
  currentAccountUser?: AccountUserType;
};

const LocationBreadcrump = (props: LocationBreadcrumpProps) => {
  const { breadcrumpData, setBreadcrumpData, currentAccountUser } = props;
  const location = useLocation();
  const { access } = useAccessControl();
  const queryClient = useQueryClient();
  const allBreadCrumps = React.useMemo(() => {
    return BreadcrumpsConfig.filter((config) => {
      switch (config.path) {
        case '/dashboard':
          return (access || []).indexOf('dashboard:read') > -1;
        case '/analytics':
          return (access || []).indexOf('analytics:read') > -1;
        case '/workflow':
          return (access || []).indexOf('workflow:read') > -1;
        case '/settings':
          return (access || []).indexOf('settings:read') > -1;
        case '/survey':
          return (access || []).indexOf('survey:read') > -1;
        case '/response':
          return (access || []).indexOf('response:read') > -1;
        case '/queue':
          return (access || []).indexOf('queue:read') > -1;
        case '/contacts':
          return (access || []).indexOf('contact:read') > -1;
        case '/lists':
          return (access || []).indexOf('contact:read') > -1;
        case '/segments':
          return (access || []).indexOf('contact:read') > -1;
        default:
          return true;
      }
    }).map((config) => {
      return {
        ...config,
        children: config.children
          ? config.children.filter((child) => {
              if (config.path === '/segments') {
                if (child.path === '/create') {
                  return (access || []).indexOf('contact:write') > -1;
                }
                return true;
              }
              if (config.path === '/workflow') {
                if (child.path === '/create') {
                  return (access || []).indexOf('workflow:write') > -1;
                }
                return true;
              }
              return true;
            })
          : undefined,
      };
    });
  }, [access]);
  const [pathsList, setPathsList] = React.useState<Path[]>([]);
  React.useEffect(() => {
    if (currentAccountUser?.user_role === 'ADMIN') {
      queryClient.refetchQueries(['account-user', currentAccountUser.user_id]);
    }
    if (setBreadcrumpData) setBreadcrumpData({ path: [] });
    const { pathname }: Location = location;
    const paths: Array<string> = pathname.split('/');
    let locationConfig: LocationPath | undefined;
    const locationPathsList: Array<Path> = [];
    paths.forEach((path) => {
      locationConfig =
        locationConfig &&
        locationConfig.children &&
        locationConfig.children.length > 0
          ? locationConfig.children.find(
              (config: LocationPath) =>
                config.path === `/${path}` || config.path === '/*',
            )
          : allBreadCrumps.find(
              (config: LocationPath) => config.path === `/${path}`,
            );
      if (locationConfig && locationConfig.path && locationConfig.label)
        locationPathsList.push({
          path: locationConfig.path,
          label: `${t(locationConfig.label)}`,
        });
    });
    setPathsList(locationPathsList);
  }, [location.pathname]);
  return (
    <StyledLocationBreadcrump>
      {pathsList.map((item: Path, index: number) => (
        <React.Fragment key={item.label}>
          {index === pathsList.length - 1 &&
          get(breadcrumpData, ['path'], []).length === 0 ? (
            <span
              className={`item ${
                pathsList.length !== 1 &&
                get(breadcrumpData, ['path'], []).length === 0
                  ? 'last'
                  : ''
              }`}
            >
              {item.label}
            </span>
          ) : (
            <>
              <Link
                to={pathsList
                  .slice(0, index + 1)
                  .map((v: Path) => v.path)
                  .join('')
                  .replace('/*', '')}
              >
                <span className="item">{item.label}</span>
              </Link>
              {index !== pathsList.length - 1 && (
                <span className="divider">
                  <Icon height="12" width="6" glyph="arrow-right" />
                </span>
              )}
            </>
          )}
        </React.Fragment>
      ))}
      {breadcrumpData &&
        breadcrumpData?.path?.map((data, index) => (
          <Fragment key={index}>
            <span className="divider">
              <Icon height="12" width="6" glyph="arrow-right" />
            </span>
            <span className={'item last'}>{data}</span>
          </Fragment>
        ))}
      {typeof breadcrumpData?.count === 'number' &&
      breadcrumpData.count >= 0 ? (
        <span className={'item last'}>&nbsp;({breadcrumpData.count})</span>
      ) : (
        <span className={'item'}>&nbsp;&nbsp;{breadcrumpData?.text || ''}</span>
      )}
    </StyledLocationBreadcrump>
  );
};

const StyledLocationBreadcrump = styled.div`
  display: flex;
  align-items: center;
  overflow: auto;
  span.item {
    overflow: hidden;
    white-space: nowrap;
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #12344d;
    user-select: none;
  }
  span.divider {
    margin: 0 12px;
  }
  span.last {
    font-weight: 400;
  }
`;
export default LocationBreadcrump;
